/**
 * What a column in somebody's spreadsheet means, and what to do with the text
 * underneath it.
 *
 * The premise of the importer is that nobody should have to reshape their file
 * before the app will read it. A list exported from the system being left
 * behind says "First Name", "FIRSTNAME", "Primary Contact First Name" or
 * "Nombre"; the phone column may be "Phone", "Mobile" or "Tel"; the consent
 * column says "Yes", "TRUE", "Y" or "1". None of that is worth a person's
 * afternoon, so the aliases and the value readers below absorb it, and the
 * mapping they produce is shown for correction rather than assumed.
 *
 * This is shared because both ends need the same answer: the browser to build
 * the preview, and the Worker to validate what the preview promised.
 */

import { idSchema } from './schemas/common.ts';
import { createCustomerSchema, updateCustomerSchema } from './schemas/customers.ts';

/**
 * A column the importer understands.
 *
 * `fullName` is the odd one out: there is no such customer field, but there is
 * almost always such a spreadsheet column, and splitting it here is the
 * difference between an import that works and one that asks the person to go
 * away and add two columns first.
 */
export const CUSTOMER_IMPORT_KEYS = [
  'id',
  'fullName',
  'firstName',
  'lastName',
  'companyName',
  'email',
  'phone',
  'billingAddressLine1',
  'billingAddressLine2',
  'billingCity',
  'billingState',
  'billingPostalCode',
  'billingCountryCode',
  'invoiceTermsDays',
  'invoicePoNumber',
  'consolidatedBilling',
  'smsMarketingEnabled',
  'emailMarketingEnabled',
  'serviceSegment',
  'internalNotes',
] as const;

export type CustomerImportKey = (typeof CUSTOMER_IMPORT_KEYS)[number];

type ValueKind = 'text' | 'boolean' | 'integer' | 'country' | 'fullName' | 'id' | 'serviceSegment';

export interface CustomerImportField {
  key: CustomerImportKey;
  /**
   * The header the export writes, and the first thing auto-matching tries — so
   * a file exported from here and re-imported maps itself with nothing to
   * correct.
   */
  label: string;
  kind: ValueKind;
  /** Other things the same column is called in the wild, normalized on use. */
  aliases: readonly string[];
}

export const CUSTOMER_IMPORT_FIELDS: readonly CustomerImportField[] = [
  {
    key: 'id',
    label: 'Customer ID',
    kind: 'id',
    // Deliberately not the bare word "id": half the exports in the world have
    // an ID column holding the *other* system's identifier, and matching that
    // would turn every row of a migration into "no customer with that ID".
    // Our own header still maps itself, which is what the round trip needs.
    aliases: ['blasto clean id'],
  },
  {
    key: 'fullName',
    label: 'Full name',
    kind: 'fullName',
    aliases: ['name', 'customer', 'client', 'contact', 'customer name', 'client name', 'contact name', 'display name'],
  },
  {
    key: 'firstName',
    label: 'First name',
    kind: 'text',
    aliases: ['first', 'fname', 'given name', 'forename', 'contact first name', 'primary contact first name'],
  },
  {
    key: 'lastName',
    label: 'Last name',
    kind: 'text',
    aliases: ['last', 'lname', 'surname', 'family name', 'contact last name', 'primary contact last name'],
  },
  {
    key: 'companyName',
    label: 'Company',
    kind: 'text',
    aliases: ['company name', 'business', 'business name', 'organization', 'organisation', 'org', 'account name'],
  },
  {
    key: 'email',
    label: 'Email',
    kind: 'text',
    aliases: ['e mail', 'email address', 'primary email', 'contact email', 'email 1', 'work email', 'home email'],
  },
  {
    key: 'phone',
    label: 'Phone',
    kind: 'text',
    aliases: [
      'phone number',
      'telephone',
      'tel',
      'mobile',
      'mobile phone',
      'cell',
      'cell phone',
      'primary phone',
      'main phone',
      'contact phone',
      'phone 1',
      'home phone',
      'work phone',
    ],
  },
  {
    key: 'billingAddressLine1',
    label: 'Address line 1',
    kind: 'text',
    aliases: [
      'address',
      'address 1',
      'addr1',
      'street',
      'street address',
      'street 1',
      'billing address',
      'billing street',
      'service address',
    ],
  },
  {
    key: 'billingAddressLine2',
    label: 'Address line 2',
    kind: 'text',
    aliases: ['address 2', 'addr2', 'street 2', 'unit', 'apt', 'apartment', 'suite', 'billing address 2'],
  },
  { key: 'billingCity', label: 'City', kind: 'text', aliases: ['town', 'locality', 'billing city'] },
  {
    key: 'billingState',
    label: 'State',
    kind: 'text',
    aliases: ['province', 'region', 'county', 'state province', 'billing state'],
  },
  {
    key: 'billingPostalCode',
    label: 'Postal code',
    kind: 'text',
    aliases: ['zip', 'zip code', 'zipcode', 'postcode', 'post code', 'billing zip', 'billing postal code'],
  },
  {
    key: 'billingCountryCode',
    label: 'Country',
    kind: 'country',
    aliases: ['country code', 'billing country'],
  },
  {
    key: 'invoiceTermsDays',
    label: 'Payment terms (days)',
    kind: 'integer',
    aliases: ['terms', 'payment terms', 'net terms', 'invoice terms', 'terms days', 'net'],
  },
  {
    key: 'invoicePoNumber',
    label: 'PO number',
    kind: 'text',
    aliases: ['po', 'po number', 'purchase order', 'purchase order number'],
  },
  {
    key: 'consolidatedBilling',
    label: 'Consolidated billing',
    kind: 'boolean',
    aliases: ['monthly billing', 'consolidated invoicing', 'monthly invoice'],
  },
  {
    key: 'smsMarketingEnabled',
    label: 'SMS marketing',
    kind: 'boolean',
    aliases: ['text marketing', 'sms opt in', 'sms consent', 'accepts texts', 'sms marketing consent'],
  },
  {
    key: 'emailMarketingEnabled',
    label: 'Email marketing',
    kind: 'boolean',
    aliases: ['newsletter', 'email opt in', 'marketing email', 'marketing emails', 'accepts email'],
  },
  {
    key: 'serviceSegment',
    label: 'Home or business',
    kind: 'serviceSegment',
    aliases: [
      'customer type',
      'client type',
      'service segment',
      'customer segment',
      'account type',
      'residential or commercial',
    ],
  },
  {
    key: 'internalNotes',
    label: 'Internal notes',
    kind: 'text',
    aliases: ['notes', 'note', 'comments', 'description', 'remarks', 'memo'],
  },
];

const FIELDS_BY_KEY = new Map(CUSTOMER_IMPORT_FIELDS.map((field) => [field.key, field]));

/**
 * The columns the export writes, in order.
 *
 * `fullName` is not among them — the export has real first and last name
 * columns, and offering both would mean writing each name twice.
 */
export type CustomerExportKey = Exclude<CustomerImportKey, 'fullName'>;

export const CUSTOMER_EXPORT_KEYS: readonly CustomerExportKey[] = CUSTOMER_IMPORT_KEYS.filter(
  (key): key is CustomerExportKey => key !== 'fullName',
);

export function customerImportField(key: CustomerImportKey): CustomerImportField {
  const field = FIELDS_BY_KEY.get(key);
  if (!field) throw new Error(`Unknown import column: ${key}`);
  return field;
}

// ---------------------------------------------------------------------------
// Matching a file's headers to those columns
// ---------------------------------------------------------------------------

/**
 * Header text reduced to the part that carries meaning.
 *
 * Case, accents, punctuation and the `*` that marks a required column in
 * somebody else's export all disappear; a parenthetical aside ("Phone (mobile)")
 * goes with them, because it qualifies the column rather than naming it.
 */
export function normalizeHeader(header: string): string {
  return header
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\([^)]*\)/g, ' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export interface ColumnMatch {
  key: CustomerImportKey;
  /**
   * How sure the match is. `exact` came from the column's own name or a known
   * alias; `likely` was inferred from the words in it and is worth a glance.
   */
  confidence: 'exact' | 'likely';
}

/** How close a header has to be to a field's words before it is offered at all. */
const LIKELY_THRESHOLD = 0.6;

/**
 * Guesses which customer column each header in a file is.
 *
 * Every header is scored against every field, then the strongest pairs are
 * taken first. Best-pair-first rather than left-to-right matters on a file
 * with both "Name" and "First Name": scored in column order, "Name" would
 * plausibly take `firstName` and leave the real first-name column with
 * nothing.
 *
 * Returns one entry per header, null where nothing convincing was found.
 */
export function matchColumns(headers: readonly string[]): Array<ColumnMatch | null> {
  const scored: Array<{
    column: number;
    key: CustomerImportKey;
    score: number;
    specificity: number;
    confidence: 'exact' | 'likely';
  }> = [];

  headers.forEach((header, column) => {
    const normalized = normalizeHeader(header);
    if (!normalized) return;
    for (const field of CUSTOMER_IMPORT_FIELDS) {
      const { score, specificity } = scoreHeader(normalized, field);
      if (score > 0) {
        scored.push({ column, key: field.key, score, specificity, confidence: score >= 0.9 ? 'exact' : 'likely' });
      }
    }
  });

  // Ties are broken by how specifically the header named the field, never by
  // the order the fields happen to be declared in. "Customer Phone" carries a
  // word of `phone`'s own name and a generic alias of `fullName` ("customer"),
  // and both cover their name entirely, so the word-coverage score is
  // identical — declaration order then handed the column to `fullName`, whose
  // value reader threw the phone number away. Preferring the field whose own
  // label was matched, and then the longer name, settles it the way a person
  // reading the header would.
  scored.sort((a, b) => b.score - a.score || b.specificity - a.specificity || a.column - b.column);

  const result: Array<ColumnMatch | null> = headers.map(() => null);
  const taken = new Set<CustomerImportKey>();
  for (const candidate of scored) {
    // One column per field, and one field per column: a spreadsheet with two
    // phone columns maps the first and leaves the second for the person to
    // point somewhere (or nowhere).
    if (taken.has(candidate.key) || result[candidate.column]) continue;
    taken.add(candidate.key);
    result[candidate.column] = { key: candidate.key, confidence: candidate.confidence };
  }

  return result;
}

/**
 * 1 for a name this field is known by, less for a header that merely reads
 * like one.
 *
 * `specificity` is how firmly the winning name identified the field, for
 * ranking headers that score the same: the number of words in that name, plus
 * one when it was the field's own label rather than one of its aliases. A
 * field's label is what the column is really called, so "Phone" in "Customer
 * Phone" outranks `fullName`'s catch-all alias "customer".
 */
function scoreHeader(normalized: string, field: CustomerImportField): { score: number; specificity: number } {
  const label = normalizeHeader(field.label);
  const names = [label, ...field.aliases.map(normalizeHeader)];
  const specificityOf = (name: string) => name.split(' ').filter(Boolean).length + (name === label ? 1 : 0);

  if (names.includes(normalized)) {
    return { score: normalized === label ? 1 : 0.95, specificity: specificityOf(normalized) };
  }

  const compact = normalized.replace(/ /g, '');
  const compactMatch = names.find((name) => name.replace(/ /g, '') === compact);
  if (compactMatch) return { score: 0.9, specificity: specificityOf(compactMatch) };

  // Nothing named it outright, so fall back to the words: "Primary Billing
  // Postal Code" carries every word of "postal code" and is almost certainly
  // it, while "Postal Delivery Route" carries one of two and is not.
  const headerWords = new Set(normalized.split(' '));
  let best = 0;
  let specificity = 0;
  for (const name of names) {
    const words = name.split(' ').filter(Boolean);
    if (words.length === 0) continue;
    const covered = words.filter((word) => headerWords.has(word)).length;
    if (covered !== words.length) continue;
    // Extra words in the header dilute the match, so a two-word alias inside a
    // six-word header scores well below one inside a three-word header.
    const ratio = words.length / headerWords.size;
    const score = LIKELY_THRESHOLD + ratio * (0.89 - LIKELY_THRESHOLD);
    const nameSpecificity = specificityOf(name);
    if (score > best || (score === best && nameSpecificity > specificity)) {
      best = score;
      specificity = nameSpecificity;
    }
  }
  return best >= LIKELY_THRESHOLD ? { score: best, specificity } : { score: 0, specificity: 0 };
}

// ---------------------------------------------------------------------------
// Turning a row of text into a customer
// ---------------------------------------------------------------------------

/** A column position and what it means, or null for "do not import this one". */
export type ColumnMapping = ReadonlyArray<CustomerImportKey | null>;

export interface BuiltRow {
  /**
   * The customer this row is about, when the file named one. Kept out of
   * `values` because it is not a field anybody edits — it says which record to
   * write, not what to write on it.
   */
  id: string | null;
  /** The create-customer payload, ready for `createCustomerSchema`. */
  values: Record<string, unknown>;
  /**
   * Cells that were understood loosely or not at all — an unrecognized country,
   * a yes/no column holding prose. Keyed by field so the preview can point at
   * the offending cell instead of failing the whole row.
   */
  issues: Array<{
    key: CustomerImportKey;
    value: string;
    reason: 'boolean' | 'integer' | 'country' | 'id' | 'serviceSegment';
  }>;
}

/**
 * Reads one spreadsheet row into the shape the create-customer route takes.
 *
 * Blank cells are left out rather than sent as null: an import is additive, and
 * a column somebody did not fill in should not clear the value an existing
 * customer already has when the row is merged onto them.
 */
export function buildCustomerRow(cells: readonly string[], mapping: ColumnMapping): BuiltRow {
  const values: Record<string, unknown> = {};
  const issues: BuiltRow['issues'] = [];
  let fullName: string | null = null;
  let id: string | null = null;

  mapping.forEach((key, column) => {
    if (!key) return;
    const raw = (cells[column] ?? '').trim();
    if (raw.length === 0) return;
    const field = customerImportField(key);

    switch (field.kind) {
      case 'id':
        // An identifier that is not one of ours names nobody. Saying so is the
        // only safe answer: treated as absent it would quietly create a second
        // copy of a customer the row was trying to update.
        if (idSchema.safeParse(raw).success) id = raw;
        else issues.push({ key, value: raw, reason: 'id' });
        return;
      case 'fullName':
        fullName = raw;
        return;
      case 'boolean': {
        const parsed = parseBoolean(raw);
        if (parsed === null) issues.push({ key, value: raw, reason: 'boolean' });
        else values[key] = parsed;
        return;
      }
      case 'integer': {
        const parsed = parseInteger(raw);
        if (parsed === null) issues.push({ key, value: raw, reason: 'integer' });
        else values[key] = parsed;
        return;
      }
      case 'country': {
        const parsed = parseCountry(raw);
        if (parsed === null) issues.push({ key, value: raw, reason: 'country' });
        else values[key] = parsed;
        return;
      }
      case 'serviceSegment': {
        const parsed = parseServiceSegment(raw);
        if (parsed === null) issues.push({ key, value: raw, reason: 'serviceSegment' });
        else values[key] = parsed;
        return;
      }
      default:
        values[key] = raw;
    }
  });

  // An explicit first or last name column always wins: a file carrying both
  // "Name" and "Last Name" means the split one, and the combined column is
  // usually a display string assembled from the others.
  if (fullName !== null) {
    const split = splitFullName(fullName);
    if (!values.firstName && split.firstName) values.firstName = split.firstName;
    if (!values.lastName && split.lastName) values.lastName = split.lastName;
  }

  return { id, values, issues };
}

/**
 * "Okafor, Dana" and "Dana Okafor" are the same person.
 *
 * With a comma the surname leads, which is how an alphabetized export writes
 * names. Without one the first word is the given name and everything after it
 * is the family name — which keeps "Maria del Carmen Ruiz" together rather than
 * dropping the two middle words that a first/middle/last split would.
 */
export function splitFullName(value: string): { firstName: string | null; lastName: string | null } {
  const trimmed = value.replace(/\s+/g, ' ').trim();
  if (!trimmed) return { firstName: null, lastName: null };

  const comma = trimmed.indexOf(',');
  if (comma >= 0) {
    const lastName = trimmed.slice(0, comma).trim();
    const firstName = trimmed.slice(comma + 1).trim();
    return { firstName: firstName || null, lastName: lastName || null };
  }

  const space = trimmed.indexOf(' ');
  if (space < 0) return { firstName: trimmed, lastName: null };
  return { firstName: trimmed.slice(0, space), lastName: trimmed.slice(space + 1).trim() || null };
}

const TRUE_WORDS = new Set(['true', 'yes', 'y', '1', 'x', 'on', 'enabled', 'opted in', 'opt in', 'subscribed', 'si']);
const FALSE_WORDS = new Set([
  'false',
  'no',
  'n',
  '0',
  'off',
  'disabled',
  'opted out',
  'opt out',
  'unsubscribed',
  'none',
]);

const RESIDENTIAL_WORDS = new Set(['residential', 'home', 'house', 'homes', 'resid', 'res', 'hogar', 'casa']);
const COMMERCIAL_WORDS = new Set([
  'commercial',
  'business',
  'biz',
  'office',
  'store',
  'negocio',
  'comercial',
  'empresa',
]);

/** Home/business as a spreadsheet writes it, or null when it is something else. */
export function parseServiceSegment(value: string): 'residential' | 'commercial' | null {
  const normalized = normalizeHeader(value);
  if (RESIDENTIAL_WORDS.has(normalized)) return 'residential';
  if (COMMERCIAL_WORDS.has(normalized)) return 'commercial';
  return null;
}

/** Every way a spreadsheet says yes or no, or null when it is saying something else. */
export function parseBoolean(value: string): boolean | null {
  const normalized = normalizeHeader(value);
  if (TRUE_WORDS.has(normalized)) return true;
  if (FALSE_WORDS.has(normalized)) return false;
  // Ticked checkboxes arrive as a mark rather than a word.
  const trimmed = value.trim();
  if (trimmed === '✓' || trimmed === '✔' || trimmed === '☑') return true;
  if (trimmed === '✗' || trimmed === '✘' || trimmed === '☐') return false;
  return null;
}

/**
 * The number inside a terms column, however it was written.
 *
 * "Net 30", "30 days" and "30" are all thirty. A cell with no digits at all is
 * not a number and says so rather than quietly importing as zero.
 *
 * Only a leading hyphen is a minus sign. Anywhere else it separates words —
 * "Net-30" is thirty days, not minus thirty, and reading it as a negative
 * failed the server's own "cannot be negative" check on an ordinary cell.
 */
export function parseInteger(value: string): number | null {
  const match = /^-?\d+|\d+/.exec(value.replace(/[,\s]/g, ''));
  if (!match) return null;
  const parsed = Number.parseInt(match[0], 10);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * The handful of country spellings worth recognizing beyond the two-letter code
 * the field actually stores. Anything else is reported rather than guessed —
 * a wrong country silently applied to ten thousand addresses is worse than ten
 * thousand rows saying which column to fix.
 */
const COUNTRY_NAMES: Readonly<Record<string, string>> = {
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  us: 'US',
  america: 'US',
  canada: 'CA',
  can: 'CA',
  mexico: 'MX',
  mex: 'MX',
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  australia: 'AU',
  aus: 'AU',
  'new zealand': 'NZ',
  ireland: 'IE',
  'puerto rico': 'PR',
};

export function parseCountry(value: string): string | null {
  const normalized = normalizeHeader(value);
  const named = COUNTRY_NAMES[normalized];
  if (named) return named;
  return /^[a-z]{2}$/.test(normalized) ? normalized.toUpperCase() : null;
}

// ---------------------------------------------------------------------------
// Validating a built row
// ---------------------------------------------------------------------------

export interface RowValidation {
  /** Field name to message, in the shape the forms and the API already use. */
  fieldErrors: Record<string, string>;
}

/**
 * Runs a built row through the same schema the create-customer route uses.
 *
 * Deliberately the very same schema rather than a copy tuned for imports: the
 * preview's promise is that these rows will save, and the only way to keep that
 * promise is to ask the question the server will ask.
 */
export function validateCustomerRow(
  values: Record<string, unknown>,
  options: {
    /**
     * The row names a customer by id, so it is an edit rather than a new
     * record. A spreadsheet of "id, city" is a perfectly ordinary way to
     * correct a list, and holding it to the create schema would reject every
     * row of it for having no name — a name the customer already has.
     *
     * The name invariant still holds; it is simply not answerable here,
     * because it is a fact about the merged result and only the server has the
     * other half of that merge.
     */
    update?: boolean;
  } = {},
): RowValidation {
  const result = (options.update ? updateCustomerSchema : createCustomerSchema).safeParse(values);
  if (result.success) return { fieldErrors: {} };

  const fieldErrors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.') || 'row';
    fieldErrors[path] ??= issue.message;
  }
  return { fieldErrors };
}

/** True when a row carries nothing worth importing. */
export function isEmptyRow(values: Record<string, unknown>): boolean {
  return Object.values(values).every((value) => value === null || value === undefined || value === '');
}
